import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  ChecklistTemplate,
  ExecutionsListResponse,
  TemplatesListResponse,
} from './types';

const KEY = {
  templates: (params: Record<string, unknown>) => ['checklist-templates', params] as const,
  template: (id: string) => ['checklist-templates', id] as const,
  executions: (params: Record<string, unknown>) => ['checklist-executions', params] as const,
};

// Templates
export function useChecklistTemplates(params: { page?: number; limit?: number; q?: string; category?: string } = {}) {
  return useQuery<TemplatesListResponse>({
    queryKey: KEY.templates(params),
    queryFn: async () => {
      const { data } = await api.get<TemplatesListResponse>('/checklists/templates', { params });
      return data;
    },
  });
}

export function useChecklistTemplate(id: string | null) {
  return useQuery<{ data: ChecklistTemplate }>({
    queryKey: id ? KEY.template(id) : ['null'],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<{ data: ChecklistTemplate }>(`/checklists/templates/${id}`);
      return data;
    },
  });
}

export function useCreateChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      category: string;
      items: Array<{
        label: string;
        description?: string;
        type: string;
        required?: boolean;
        photoRequired?: boolean;
        sortOrder: number;
        group?: string;
      }>;
    }) => {
      const { data } = await api.post<{ data: ChecklistTemplate }>('/checklists/templates', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
  });
}

export function useUpdateChecklistTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data } = await api.patch<{ data: ChecklistTemplate }>(`/checklists/templates/${id}`, input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
  });
}

export function useDeleteChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/checklists/templates/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
  });
}

// Executions
export function useChecklistExecutions(params: { page?: number; limit?: number; templateId?: string; vehicleId?: string; driverId?: string; status?: string; q?: string } = {}) {
  return useQuery<ExecutionsListResponse>({
    queryKey: KEY.executions(params),
    queryFn: async () => {
      const { data } = await api.get<ExecutionsListResponse>('/checklists/executions', { params });
      return data;
    },
  });
}
